"use client";

import { useState, useEffect, useRef } from "react";
import Image from "next/image";
import Link from "next/link";
import { Calculator, X, ChevronDown, MessageCircle, Calendar, Menu } from "lucide-react";

// ═══════════════════════════════════════════════════════════════════════════
// DATA STRUCTURE
// ═══════════════════════════════════════════════════════════════════════════

const NAV_ITEMS = [
  {
    key: "business",
    label: "Business Setup",
    mega: {
      columns: [
        {
          title: "Setup Types",
          links: [
            { label: "Free Zone", href: "/free-zone" },
            { label: "Mainland", href: "/mainland" },
            { label: "Offshore", href: "/offshore" },
            // { label: "Business Activities", href: "/free-zone/activities" },
          ],
        },
        // {
        //   title: "Discover",
        //   links: [
        //     { label: "Business Setup in Dubai", href: "/discover/business-setup-in-dubai" },
        //     { label: "Trade Licence Guide", href: "/discover/dubai-trade-licence-guide" },
        //     { label: "E-Commerce Licence", href: "/discover/e-commerce-licence-in-dubai" },
        //   ],
        // },
      ],
    },
  },
  {
    key: "corporate",
    label: "Corporate Services",
    mega: {
      columns: [
        {
          title: "Essential Services",
          links: [
            { label: "Bank Account Opening", href: "/essential-services/bank-account-opening-in-uae" },
            { label: "Accounting Services", href: "/essential-services/accounting-services" },
            { label: "VAT & Tax Consultancy", href: "/essential-services/corporate-tax" },
            { label: "Compliance Services", href: "/essential-services/compliance-services" },
            { label: "Health Insurance", href: "/essential-services/health-insurance" },
          ],
        },
        {
          title: "Operational Support",
          links: [
            { label: "IT Services", href: "/essential-services/it-services" },
            { label: "Digital Marketing Services", href: "/essential-services/digital-marketing" },
            // { label: "Tourist Visa", href: "/support-services/tourist-visa" },
            { label: "PRO Services", href: "/support-services/pro-services" },
            { label: "Golden Visa UAE", href: "/support-services/golden-visa-uae" },
            { label: "Workforce Solutions", href: "/workforce-solutions" },
            { label: "Europe Work Permit", href: "/europe-work-permit" },
          ],
        },
        // {
        //   title: "Free Zones",
        //   links: [
        //     { label: "ADGM", href: "/free-zone/locations/adgm" },
        //     { label: "Ajman Free Zone", href: "/free-zone/locations/ajman" },
        //     { label: "DAFZA", href: "/free-zone/locations/dafza" },
        //     { label: "Dubai CommerCity", href: "/free-zone/locations/dcc" },
        //     { label: "Dubai Design District (D3)", href: "/free-zone/locations/d3" },
        //   ],
        // },
      ],
    },
  },
];

const SIMPLE_LINKS = [
  { label: "Visa Services", href: "/services" },
  // { label: "Pricing", href: "/pricing" },
  { label: "About", href: "/about" },
  { label: "Contact", href: "/contact" },
];

// ═══════════════════════════════════════════════════════════════════════════
// MEGA MENU COLUMN COMPONENT
// ═══════════════════════════════════════════════════════════════════════════

function MegaColumn({ col }) {
  if (col.isConnect) {
    return (
      <div className="">
        <h4
          className="font-bold tracking-[0.3em] uppercase text-white/80 mb-3 pb-2 border-b border-white/10"
          style={{
            fontSize: "clamp(10px, 0.8vw, 12px)"
          }}
        >
          {col.title}
        </h4>
        <div className="flex flex-col gap-2.5">
          {col.cards.map((card, index) => {
            const Icon = card.icon;
            return (
              <Link
                key={`${card.href}-${index}`}
                href={card.href}
                className="group bg-amber-950/10 hover:bg-amber-950/20 border border-amber-900/20 hover:border-amber-700/40 rounded-xl p-3.5 flex items-start gap-3 transition-all duration-200"
              >
                <div className="w-8 h-8 rounded-lg bg-[#B8893A] flex items-center justify-center flex-shrink-0">
                  <Icon className="w-4 h-4 text-black" />
                </div>
                <div>
                  <p
                    className="font-bold text-white mb-0.5 tracking-wider uppercase"
                    style={{ fontSize: "clamp(10px, 0.8vw, 12px)" }}
                  >
                    {card.title}
                  </p>
                  <p
                    className="text-white/60 leading-tight"
                    style={{ fontSize: "clamp(9px, 0.7vw, 11px)" }}
                  >
                    {card.desc}
                  </p>
                </div>
              </Link>
            );
          })}
        </div>
      </div>
    );
  }

  return (
    <div>
      <h4
        className="font-bold tracking-[0.3em] uppercase text-white mb-3 pb-2 border-b border-white/10"
        style={{ fontSize: "clamp(10px, 0.8vw, 12px)" }}
      >
        {col.title}
      </h4>
      <div className="flex flex-col">
        {col.links.map((link, index) => (
          <Link
            key={`${link.label}-${link.href}-${index}`}
            href={link.href}
            className="group flex items-center justify-between py-2 px-2 rounded-lg hover:bg-white/10 text-white hover:text-white transition-all duration-200"
          >
            <span
              className="font-bold tracking-wide uppercase"
              style={{ fontSize: "clamp(10px, 0.8vw, 12px)" }}
            >
              {link.label}
            </span>
            <span className="text-sm opacity-0 group-hover:opacity-100 -translate-x-1 group-hover:translate-x-0 transition-all duration-200">
              →
            </span>
          </Link>
        ))}
      </div>
    </div>
  );
}

// ═══════════════════════════════════════════════════════════════════════════
// DESKTOP NAV ITEM WITH MEGA MENU
// ═══════════════════════════════════════════════════════════════════════════

function DesktopNavItem({ item }) {
  const [open, setOpen] = useState(false);
  const closeTimer = useRef(null);
  const colCount = item.mega?.columns?.length || 0;
  const gridCols = colCount === 4 ? "grid-cols-4" : colCount === 3 ? "grid-cols-3" : colCount === 2 ? "grid-cols-2" : "grid-cols-1";
  const menuWidth = colCount >= 4 ? "w-[min(1200px,calc(100vw-3rem))] min-w-[1100px]" : colCount === 3 ? "w-[min(1000px,calc(100vw-3rem))] min-w-[900px]" : colCount === 2 ? "w-[min(760px,calc(100vw-3rem))] min-w-[620px]" : "w-[min(500px,calc(100vw-3rem))] min-w-[320px]";

  const openMenu = () => {
    if (closeTimer.current) {
      clearTimeout(closeTimer.current);
      closeTimer.current = null;
    }
    setOpen(true);
  };

  const closeMenu = () => {
    if (closeTimer.current) {
      clearTimeout(closeTimer.current);
    }
    closeTimer.current = setTimeout(() => setOpen(false), 150);
  };

  useEffect(() => {
    return () => {
      if (closeTimer.current) {
        clearTimeout(closeTimer.current);
      }
    };
  }, []);

  return (
    <div
      className="relative"
      onMouseEnter={openMenu}
      onMouseLeave={closeMenu}
    >
      <button
        onClick={() => setOpen((prev) => !prev)}
        className={`
          flex items-center gap-1 px-4 py-2 rounded-lg
          font-bold tracking-wide uppercase
          transition-all duration-200
          ${open ? "bg-white/10 text-white" : "text-white  hover:text-white"}
        `}
        style={{
          fontSize: "clamp(12px, 1vw, 14px)"
        }}
      >
        {item.label}
        <ChevronDown
          className={`w-3.5 h-3.5 transition-transform duration-300 ${open ? "rotate-180" : ""
            }`}
        />
      </button>

      {/* Mega Menu Dropdown */}
      <div
        className={`
          absolute top-[calc(100%+0.2rem)] left-1/2 -translate-x-1/2
          bg-black/95 backdrop-blur-xl border border-amber-900/30 rounded-2xl
          shadow-2xl shadow-black/60 p-6
          transition-all duration-300 z-50 
          ${menuWidth}
          ${open
            ? "opacity-100 visible translate-y-0"
            : "opacity-0 invisible translate-y-2 pointer-events-none"
          }
        `}
        onMouseEnter={openMenu}
        onMouseLeave={closeMenu}
      >
        {/* Arrow pointer */}
        <div className="absolute -top-1.5 left-1/2 -translate-x-1/2 w-3 h-3 bg-black border-l border-t border-amber-900/30 rotate-45" />

        <div className={`grid ${gridCols} gap-6`}>
          {item.mega.columns.map((col, i) => (
            <MegaColumn key={i} col={col} />
          ))}
        </div>
      </div>
    </div>
  );
}

// ═══════════════════════════════════════════════════════════════════════════
// MOBILE ACCORDION SECTION
// ═══════════════════════════════════════════════════════════════════════════

function MobileSection({ section, onClose }) {
  const [open, setOpen] = useState(false);

  return (
    <div className="mb-2">
      <p className="text-[9px] font-bold tracking-[0.3em] uppercase text-white mb-2 px-1">
        {section.sectionLabel}
      </p>
      <button
        onClick={() => setOpen(!open)}
        className={`
          w-full flex items-center justify-between px-4 py-3.5 rounded-xl
          border border-white/10 transition-all duration-200
          ${open ? "bg-white/10" : "bg-white/5"}
        `}
      >
        <span className="text-[12px] font-bold tracking-wide uppercase text-white/80">
          {section.title}
        </span>
        <ChevronDown
          className={`w-4 h-4 text-white transition-transform duration-300 ${open ? "rotate-180" : ""
            }`}
        />
      </button>

      <div
        className={`
          overflow-hidden transition-all duration-300 ease-in-out
          ${open ? "max-h-96 mt-2" : "max-h-0"}
        `}
      >
        <div className="pl-2 space-y-1">
          {section.links.map((link, index) => (
            <Link
              key={`${link.label}-${link.href}-${index}`}
              href={link.href}
              onClick={onClose}
              className="group flex items-center justify-between px-3 py-2 rounded-lg hover:bg-white/10 transition-all duration-150"
            >
              <span className="text-[11px] font-bold tracking-wide uppercase text-white group-hover:text-white">
                {link.label}
              </span>
              <span className="text-sm text-white group-hover:text-white">→</span>
            </Link>
          ))}
        </div>
      </div>
    </div>
  );
}

// ═══════════════════════════════════════════════════════════════════════════
// MAIN NAVBAR COMPONENT
// ═══════════════════════════════════════════════════════════════════════════

export default function Navbar() {
  const [drawerOpen, setDrawerOpen] = useState(false);

  useEffect(() => {
    document.body.style.overflow = drawerOpen ? "hidden" : "";
    return () => {
      document.body.style.overflow = "";
    };
  }, [drawerOpen]);

  const closeDrawer = () => setDrawerOpen(false);

  // Mobile sections data
  const MOBILE_SECTIONS = [
    {
      key: "business-setup",
      sectionLabel: "Business Setup",
      title: "Setup Types",
      links: NAV_ITEMS[0].mega.columns[0]?.links || [],
    },
    {
      key: "essential",
      sectionLabel: "Corporate Services",
      title: "Essential Services",
      links: NAV_ITEMS[1].mega.columns[0]?.links || [],
    },
    {
      key: "support",
      sectionLabel: "Support",
      title: "Business Support",
      links: NAV_ITEMS[1].mega.columns[1]?.links || [],
    },
  ];

  return (
    <>
      {/* ═══════════════════════════════════════════════════════════════════ */}
      {/* MAIN NAVBAR */}
      {/* ═══════════════════════════════════════════════════════════════════ */}
      <nav className="fixed top-0 left-0 right-0 z-50 bg-black border-b border-amber-900/30 h-[72px]" style={{ fontFamily: "var(--font-montserrat)" }}>
        <div className="main-container h-full flex items-center justify-between relative px-4 sm:px-6 lg:px-8">

          {/* Logo */}
          <Link href="/" className="flex items-center gap-3 z-10 flex-shrink-0">
            <div className="relative w-12 h-12">
              <Image
                src="/white-logo-png.png"
                alt="AWS Businessmen Services"
                fill
                className="object-contain"
                priority
              />
            </div>
            <div className="flex flex-col leading-none font-brand">
              <div
                className="font-extrabold tracking-[0.08em] uppercase text-[#B8893A] font-brand"
                style={{ fontSize: "clamp(12px, 1.5vw, 19px)" }}
              >
                AWS  Businessmen
              </div>
              <span
                className="uppercase text-[#B8893A] tracking-[0.3em] font-brand"
                style={{ fontSize: "clamp(8px, 0.8vw, 12px)" }}
              >
                Services
              </span>
            </div>
          </Link>


          {/* Desktop Navigation */}
          <div className="hidden lg:flex items-center gap-1">
            {NAV_ITEMS.map((item) => (
              <DesktopNavItem key={item.key} item={item} />
            ))}

            {SIMPLE_LINKS.map((link) => (
              <Link
                key={link.href}
                href={link.href}
                className="px-4 py-2 rounded-lg text-[12px] font-bold tracking-wide uppercase text-white hover:text-white hover:bg-white/10 transition-all duration-200"
              >
                {link.label}
              </Link>
            ))}

            <Link
              href="/calculator"
              className="ml-2 flex items-center gap-2 px-3 py-2 rounded-xl bg-[#B8893A] text-white text-[11px] font-black tracking-wider uppercase shadow-lg shadow-amber-900/40 hover:shadow-xl hover:shadow-amber-800/50 hover:-translate-y-0.5 transition-all duration-200"
            >
              <Calculator className="w-3.5 h-3.5" />
              Cost Calculator
            </Link>
          </div>

          {/* Mobile Hamburger */}
          <button
            onClick={() => setDrawerOpen(true)}
            aria-label="Open menu"
            className="lg:hidden flex flex-col gap-1.5 p-1 z-10"
          >
            <span className="block h-0.5 w-6 bg-[#fafafa] rounded" />
            <span className="block h-0.5 w-5 bg-[#fafafa] rounded" />
            <span className="block h-0.5 w-4 bg-[#fafafa] rounded" />
          </button>
        </div>
      </nav>

      {/* ═══════════════════════════════════════════════════════════════════ */}
      {/* MOBILE DRAWER */}
      {/* ═══════════════════════════════════════════════════════════════════ */}

      {/* Backdrop */}
      <div
        onClick={closeDrawer}
        className={`
          fixed inset-0 bg-black/80 backdrop-blur-md z-60 transition-opacity duration-300
          ${drawerOpen ? "opacity-100 visible" : "opacity-0 invisible"}
        `}
      />

      {/* Drawer Panel */}
      <aside
        className={`
          fixed top-0 bottom-0 right-0 z-[70] w-[85%] max-w-sm
          bg-black/98 backdrop-blur-2xl border-l border-amber-900/30
          shadow-2xl overflow-y-auto transition-transform duration-500 ease-out
          ${drawerOpen ? "translate-x-0" : "translate-x-full"}
        `}
        style={{ fontFamily: "var(--font-montserrat)" }}
      >
        <div className="p-6 pb-10 flex flex-col gap-0">

          {/* Header */}
          <div className="flex items-center justify-between mb-6">
            <div className="flex items-center gap-3">
              <div className="relative w-10 h-10">
                <Image
                  src="/white-text-logo-png.png"
                  alt="Logo"
                  fill
                  className="object-contain"
                />
              </div>
              <div className="font-brand">
                <p className="text-[11px] font-black tracking-wider uppercase text-white">
                  AWS Businessmen
                </p>
                <p className="text-[8px] font-semibold tracking-[0.3em] uppercase text-white/60 mt-0.5">
                  Services
                </p>
              </div>
            </div>
            <button
              onClick={closeDrawer}
              aria-label="Close navigation menu"
              className="w-9 h-9 rounded-full bg-white/5 border border-white/10 flex items-center justify-center text-white/50 hover:bg-white/10 transition-all"
            >
              <X className="w-4 h-4" />
            </button>
          </div>

          <div className="h-px bg-white/5 mb-6" />

          {/* Accordion Sections */}
          <div className="flex flex-col gap-2 mb-3">
            {MOBILE_SECTIONS.map((section) => (
              <MobileSection key={section.key} section={section} onClose={closeDrawer} />
            ))}
          </div>

          <div className="h-px bg-white/5 my-4" />

          {/* Simple Links */}
          <p className="text-[9px] font-bold tracking-[0.3em] uppercase text-white/60 mb-3 px-1">
            Other Links
          </p>
          <div className="flex flex-col gap-1 mb-6">
            {SIMPLE_LINKS.map((link) => (
              <Link
                key={link.href}
                href={link.href}
                onClick={closeDrawer}
                className="flex items-center justify-between px-3 py-2 rounded-xl text-[13px] font-bold tracking-wide uppercase text-white/70 hover:text-white transition-colors"
              >
                <span>{link.label}</span>
                <span>→</span>
              </Link>
            ))}
            <Link
              href="/contact"
              onClick={closeDrawer}
              className="flex items-center justify-center px-3 py-2 rounded-xl bg-[#B8893A] hover:bg-[#A87833] text-white text-[12px] font-black tracking-wide uppercase transition-colors"
            >
              Contact Us
            </Link>
          </div>

          {/* CTA Button */}
          <Link
            href="/calculator"
            onClick={closeDrawer}
            className="flex items-center justify-center gap-3 px-4 py-2 rounded-2xl bg-[#B8893A] text-white text-[13px] font-black tracking-[0.2em] uppercase shadow-xl shadow-amber-900/40 hover:shadow-2xl hover:shadow-amber-800/60 transition-all mt-auto"
          >
            <Calculator className="w-5 h-5" />
            Cost Calculator
          </Link>
        </div>
      </aside>
    </>
  );
}
